import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CarrierService } from '../../../../../services/forwarding/carrier.service';
import { Carrier, CreateCarrierDto, UpdateCarrierDto } from '../../../../../models/forwarding/carrier.model';

@Component({
  selector: 'app-carriers-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './carriers-tab.html',
  styleUrl: './carriers-tab.css'
})
export class CarriersTabComponent implements OnInit, OnDestroy {
  showAddModal = false;
  showEditModal = false;
  showCarrierChargesModal = false;
  carriers: Carrier[] = [];
  filteredCarriers: Carrier[] = [];
  searchTerm = '';
  private subscriptions = new Subscription();

  // Carrier-Specific Charges Modal
  selectedCarrierForCharges: Carrier | null = null;
  selectedCarriersForCharges: Carrier[] = [];
  availableCarriersForSelection: Carrier[] = [];
  selectedCarrierToAdd: number | string = '';

  formData: CreateCarrierDto = {
    code: '',
    name: '',
    modes: [],
    regions: [],
    notes: '',
    isActive: true
  };

  editFormData: UpdateCarrierDto = {
    code: '',
    name: '',
    modes: [],
    regions: [],
    notes: '',
    isActive: true
  };

  currentEditId: number | null = null;

  // Available modes and regions for selection
  availableModes = ['FCL', 'LCL', 'Air', 'RORO'];
  availableRegions = ['Asia', 'Europe', 'North America', 'South America', 'Africa', 'Oceania', 'Middle East'];

  // Custom region input
  customRegionInput = '';

  constructor(private carrierService: CarrierService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.carrierService.getCarriers().subscribe(carriers => {
        this.carriers = carriers;
        this.filteredCarriers = carriers;
        console.log('[CarriersTab] Carriers updated:', carriers.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openAddCarrierModal(): void {
    this.showAddModal = true;
    this.resetForm();
    console.log('[CarriersTab] Add modal opened');
  }

  closeAddCarrierModal(): void {
    this.showAddModal = false;
    this.resetForm();
  }

  openEditCarrierModal(carrier: Carrier): void {
    this.showEditModal = true;
    this.currentEditId = carrier.id;
    this.editFormData = {
      code: carrier.code,
      name: carrier.name,
      modes: carrier.modes ? [...carrier.modes] : [],
      regions: carrier.regions ? [...carrier.regions] : [],
      notes: carrier.notes || '',
      isActive: carrier.isActive
    };
    console.log('[CarriersTab] Edit modal opened for carrier:', carrier.id);
  }

  closeEditCarrierModal(): void {
    this.showEditModal = false;
    this.currentEditId = null;
    this.editFormData = { code: '', name: '', modes: [], regions: [], notes: '', isActive: true };
  }

  filterCarriers(): void {
    const search = this.searchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredCarriers = this.carriers;
      return;
    }

    this.filteredCarriers = this.carriers.filter(c =>
      c.code.toLowerCase().includes(search) ||
      c.name.toLowerCase().includes(search) ||
      (c.modes && c.modes.some(m => m.toLowerCase().includes(search))) ||
      (c.regions && c.regions.some(r => r.toLowerCase().includes(search)))
    );
  }

  createCarrier(): void {
    if (!this.formData.code || !this.formData.name) {
      alert('Please fill in all required fields (Code and Name)');
      return;
    }

    this.carrierService.create(this.formData).subscribe({
      next: (carrier) => {
        console.log('[CarriersTab] Carrier created successfully:', carrier);
        this.closeAddCarrierModal();
      },
      error: (error) => {
        console.error('[CarriersTab] Error creating carrier:', error);
        alert('Error creating carrier. Please try again.');
      }
    });
  }

  updateCarrier(): void {
    if (!this.currentEditId || !this.editFormData.code || !this.editFormData.name) {
      alert('Please fill in all required fields');
      return;
    }

    this.carrierService.update(this.currentEditId, this.editFormData).subscribe({
      next: (carrier) => {
        console.log('[CarriersTab] Carrier updated successfully:', carrier);
        this.closeEditCarrierModal();
      },
      error: (error) => {
        console.error('[CarriersTab] Error updating carrier:', error);
        alert('Error updating carrier. Please try again.');
      }
    });
  }

  deleteCarrier(id: number): void {
    if (!confirm('Are you sure you want to delete this carrier?')) {
      return;
    }

    this.carrierService.delete(id).subscribe({
      next: () => {
        console.log('[CarriersTab] Carrier deleted successfully');
      },
      error: (error) => {
        console.error('[CarriersTab] Error deleting carrier:', error);
        alert('Error deleting carrier. Please try again.');
      }
    });
  }

  toggleMode(mode: string, isEdit: boolean = false): void {
    const targetArray = isEdit ? this.editFormData.modes : this.formData.modes;
    if (!targetArray) return;

    const index = targetArray.indexOf(mode);
    if (index > -1) {
      targetArray.splice(index, 1);
    } else {
      targetArray.push(mode);
    }
  }

  toggleRegion(region: string, isEdit: boolean = false): void {
    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    if (!targetArray) return;

    const index = targetArray.indexOf(region);
    if (index > -1) {
      targetArray.splice(index, 1);
    } else {
      targetArray.push(region);
    }
  }

  isModeSelected(mode: string, isEdit: boolean = false): boolean {
    const targetArray = isEdit ? this.editFormData.modes : this.formData.modes;
    return targetArray ? targetArray.includes(mode) : false;
  }

  isRegionSelected(region: string, isEdit: boolean = false): boolean {
    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    return targetArray ? targetArray.includes(region) : false;
  }

  addCustomRegion(isEdit: boolean = false): void {
    const customRegion = this.customRegionInput.trim();
    if (!customRegion) {
      return;
    }

    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    if (!targetArray) return;

    // Check if region already exists (case insensitive)
    const exists = targetArray.some(r => r.toLowerCase() === customRegion.toLowerCase());
    if (exists) {
      alert('This region already exists');
      return;
    }

    // Add to selected regions
    targetArray.push(customRegion);

    // Clear input
    this.customRegionInput = '';

    console.log('[CarriersTab] Custom region added:', customRegion);
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  private resetForm(): void {
    this.formData = {
      code: '',
      name: '',
      modes: [],
      regions: [],
      notes: '',
      isActive: true
    };
    this.customRegionInput = '';
  }

  // Carrier-Specific Charges Modal Methods
  openCarrierChargesModal(carrier: Carrier): void {
    this.selectedCarrierForCharges = carrier;
    this.selectedCarriersForCharges = [];
    this.selectedCarrierToAdd = '';

    // Get all active carriers except the current one
    this.availableCarriersForSelection = this.carriers.filter(c =>
      c.isActive && c.id !== carrier.id
    );

    this.showCarrierChargesModal = true;
    console.log('[CarriersTab] Carrier-Specific Charges modal opened for:', carrier.name);
  }

  closeCarrierChargesModal(): void {
    this.showCarrierChargesModal = false;
    this.selectedCarrierForCharges = null;
    this.selectedCarriersForCharges = [];
    this.availableCarriersForSelection = [];
    this.selectedCarrierToAdd = '';
  }

  addCarrierToSelection(): void {
    if (!this.selectedCarrierToAdd) {
      return;
    }

    const carrierId = typeof this.selectedCarrierToAdd === 'string' ?
      parseInt(this.selectedCarrierToAdd) : this.selectedCarrierToAdd;

    // Check if carrier is already selected
    if (this.selectedCarriersForCharges.some(c => c.id === carrierId)) {
      console.log('[CarriersTab] Carrier already selected');
      this.selectedCarrierToAdd = '';
      return;
    }

    // Find the carrier and add to selection
    const carrier = this.carriers.find(c => c.id === carrierId);
    if (carrier) {
      this.selectedCarriersForCharges.push(carrier);
      console.log('[CarriersTab] Carrier added to selection:', carrier.name);
    }

    // Reset dropdown
    this.selectedCarrierToAdd = '';
  }

  removeCarrierFromSelection(carrierId: number): void {
    this.selectedCarriersForCharges = this.selectedCarriersForCharges.filter(c => c.id !== carrierId);
    console.log('[CarriersTab] Carrier removed from selection');
  }

  saveCarrierCharges(): void {
    if (this.selectedCarriersForCharges.length === 0) {
      alert('Please select at least one carrier');
      return;
    }

    console.log('[CarriersTab] Saving carrier charges for:', this.selectedCarriersForCharges);
    // TODO: Implement actual save logic here

    this.closeCarrierChargesModal();
  }
}
